import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { ElementRef, Injectable } from '@angular/core';

export interface PositionCoordinates {
  top: string;
  left: string;
  rawLeft: number;
  rawTop: number;
}

@Injectable({ providedIn: 'root' })
export class PVFloorSpaceOverlayHelper {
  private _elementRef: ElementRef;
  private _evt: MouseEvent;
  private _positionCoordinates = new BehaviorSubject<PositionCoordinates>({} as PositionCoordinates);
  positionCoordinates$ = this._positionCoordinates.asObservable();

  private _updateVisibility = new BehaviorSubject(false);
  isVisible$ = combineLatest([this._updateVisibility.asObservable(), this.positionCoordinates$]).pipe(
    map(([showOverlay]) => showOverlay)
  );

  private _keepOpen = false;

  setElement(elementRef: ElementRef) {
    this._elementRef = elementRef;
  }

  setEvent(evt: MouseEvent) {
    this._evt = evt;
  }

  isVisible() {
    return this._updateVisibility.value;
  }

  shouldKeepOpen() {
    return this._keepOpen;
  }

  setKeepOpen(v: boolean) {
    this._keepOpen = v;
  }

  updateVisibility(v: boolean) {
    this._updateVisibility.next(v);
  }

  /**
   * Function that will use the current postion as an starting point when the width change
   * to move the elemet to the left side of the screen if the new width will overflow the screen
   * @param event
   * @param element
   */
  recalculatePosition() {
    const evt = this._evt;
    const elementRef = this._elementRef;
    const overlayWidth = elementRef?.nativeElement?.clientWidth ?? 700;
    const currentPosition = this._positionCoordinates.getValue();

    // To Avoid overflow on the right side of the page
    // Check if the current left position + the new width will overflow the curren screen to the righ side
    if (currentPosition.rawLeft + overlayWidth >= evt.view?.document?.body?.clientWidth) {
      // Get how many of the div will be hidden
      const difference = currentPosition.rawLeft + overlayWidth - evt.view?.document?.body?.clientWidth;
      // From the current location move the item to the left just the enough to do not hide the conent and leave a margin to the right of 20px
      const left = currentPosition.rawLeft - difference - 20;

      const top = this.#recalculateTopPositon(evt, elementRef);
      this._positionCoordinates.next({
        ...currentPosition,
        left: `${left}px`,
        rawLeft: left,
        top: `${top}px`,
        rawTop: top,
      });

      return;
    }

    // Only update the top when it is neccessary to add a gap in the bottom
    const top = this.#recalculateTopPositon(evt, elementRef);
    if (currentPosition.rawTop !== top) {
      this._positionCoordinates.next({
        ...currentPosition,
        top: `${top}px`,
        rawTop: top,
      });
    }
  }

  #recalculateTopPositon(event?: MouseEvent, element?: ElementRef) {
    const evt = event ?? this._evt;
    const elementRef = element ?? this._elementRef;
    const overlayHeight = elementRef?.nativeElement?.clientHeight ?? 360;
    const windowHeight = evt.view?.innerHeight ?? 0;
    const currentPosition = this._positionCoordinates.getValue();

    // Get the position of the cursor base on the whole document measurements
    const calcTop =
      (evt.target as HTMLElement)?.offsetTop + (evt.target as HTMLElement)?.['getBoundingClientRect']?.()?.height;

    // To Avoid overflow on the bottom side of the page
    // Check if the current top position + the new height will overflow the curren screen to the bottom
    if (calcTop + overlayHeight >= windowHeight) {
      // From the current location move the item to the top just the enough to do not hide the conent and leave a margin in the botrom of 20px
      const top = currentPosition.rawTop - 20;
      return top;
    }

    return currentPosition.rawTop;
  }

  updatePosition(event?: MouseEvent, element?: ElementRef) {
    const evt = event ?? this._evt;
    const elementRef = element ?? this._elementRef;
    const overlayHeight = elementRef?.nativeElement?.clientHeight ?? 360;
    const overlayWidth = elementRef?.nativeElement?.clientWidth ?? 700;
    const windowHeight = evt.view?.innerHeight ?? 0;

    // Get the position of the cursor base on the whole document measurements
    let top =
      (evt.target as HTMLElement)?.offsetTop + (evt.target as HTMLElement)?.['getBoundingClientRect']?.()?.height;
    let left = evt?.pageX;

    let isOverflowBottom = false;
    // Avoid overflow on the bottom of the page
    if (overlayHeight + evt.clientY >= windowHeight) {
      // Get the overflow content that will not be shown + a little margin in the bottom
      // From the View Port Height substract the the cursor position and the Overflow height to get the overflow content
      const marginBottom = 20; // This margin is from the bottom of the modal to the end of the page
      const windowDif = windowHeight - evt.clientY;
      const difference = overlayHeight - windowDif;
      top = top - difference - marginBottom;
      isOverflowBottom = true;

      // Avoid overlaping with cursor and the Overlay-ish
      if (left <= overlayWidth || left === evt?.pageX) {
        left = left + 40;
      }
    }

    // Avoid overflow on the right side of the page
    if (left + overlayWidth >= evt.view?.document?.body?.clientWidth) {
      left = isOverflowBottom
        ? evt.pageX - overlayWidth - 40
        : evt.view?.document?.body?.clientWidth - overlayWidth - 20;
    }

    this._positionCoordinates.next({
      left: `${left}px`,
      top: `${top}px`,
      rawLeft: left,
      rawTop: top,
    });
  }

  cleanPositions() {
    this._positionCoordinates.next({} as PositionCoordinates);
    this._keepOpen = false;
  }
}
